/**
 * SERVER CLOCK — Cristian's algorithm, parameterised by URL.
 *
 * This is `lucky7/clockSync.ts` with the two things that stopped it being reusable fixed:
 *   1. the endpoint is a constructor argument instead of a hardcoded `/api/lucky7/time`;
 *   2. the sample goes through the shared `api` client, so a 401 mid-session refreshes the
 *      access token and re-issues instead of poisoning the sync with a silent failure.
 * The MATH is unchanged and is the part that matters (code map C11).
 *
 * WHY IT EXISTS. Every shared table is a money clock: the betting window closes at a
 * `locks_at` stamped by MySQL's `NOW(3)`, and a client whose device clock is minutes fast
 * would grey the BET button early — or, worse, keep it lit past the lock and hand the player
 * a 409 for a tap they made "in time". So the countdown is anchored to the SERVER, not the
 * device:
 *
 *   1. Round-trip `GET <url>` and estimate the server clock at the request MIDPOINT
 *      (symmetric-latency assumption) → `serverTime ≈ tServer + rtt/2`. That is Cristian.
 *   2. Anchor the resulting offset to `performance.now()`, the browser's MONOTONIC clock,
 *      which does not move when the OS re-syncs NTP or the user edits their clock. So
 *      `serverNow() = performance.now() + offset` can never jump.
 *   3. Keep the LOWEST-RTT sample of a burst (NTP's minimum-delay filter: the least-delayed
 *      round trip is the one whose midpoint assumption is most nearly true), and re-sync
 *      periodically and whenever the tab comes back to the foreground.
 *
 * HONEST LIMIT: sub-millisecond sync over HTTP is physically impossible — jitter is
 * milliseconds. In practice this locks a countdown to the server within a few ms and keeps
 * it there, which is all a 12-second betting window needs.
 *
 * TIME DOMAINS. Three of them, and mixing them is the classic bug:
 *   • SERVER epoch ms  — what `/state` and `/time` return, what the DB stamped.
 *   • perf ms          — `performance.now()`, monotonic, what the shipped game Pages
 *                        subtract from directly.
 *   • wall epoch ms    — `Date.now()`; used ONLY as the pre-sync fallback.
 * `toPerf()` / `toServer()` are the only sanctioned conversions.
 */

import { api } from '@baaligames/shared-ui/lib/api';

/* ── Pure, unit-testable math (no I/O) ─────────────────────────────────────────────────── */

/** Offset to add to `performance.now()` to get server epoch ms, from ONE sample: the
 *  server-reported epoch plus the two monotonic timestamps bracketing the request. */
export function computeOffset(serverEpochMs: number, t0: number, t1: number): number {
  // The server read its clock at ~the request midpoint; the midpoint in the
  // performance.now() domain is (t0 + t1) / 2.
  return serverEpochMs - (t0 + t1) / 2;
}

/** Server epoch ms for a monotonic reading + offset. */
export function applyOffset(perfNow: number, offset: number): number {
  return perfNow + offset;
}

export type ClockSync = {
  /** Run a burst of samples; keep the offset from the lowest-RTT one. */
  sync(samples?: number): Promise<boolean>;
  /** Server epoch ms, monotonic-anchored. `Date.now()` before the first sample. */
  serverNow(): number;
  /** Server epoch ms → the `performance.now()` domain the game Pages render in. */
  toPerf(serverMs: number): number;
  /** `performance.now()` domain → server epoch ms. */
  toServer(perfMs: number): number;
  /** True once at least one `/time` round trip has landed. */
  isSynced(): boolean;
  /** The RTT of the sample the current offset came from (ms). */
  lastRtt(): number;
  /** The current offset (server epoch − performance.now()). */
  offset(): number;
  /**
   * Feed a server timestamp that arrived on ANOTHER endpoint (`/state.serverNow`), bracketed
   * by the same two monotonic readings. Used only while unsynced, so a page that has not yet
   * completed a `/time` burst still renders a countdown anchored to the server rather than to
   * the device. A real `/time` sample always wins.
   */
  noteFallback(serverEpochMs: number, t0: number, t1: number): void;
  /** Initial burst, then periodic re-sync + a re-sync on tab refocus. Returns stop(). */
  startAutoSync(periodMs?: number): () => void;
  /** Stop everything; further samples are ignored. */
  destroy(): void;
};

/**
 * Build a clock synchroniser against one game's `/time`.
 *
 * @param url path as the shared api client wants it — WITHOUT the `/api` prefix
 *            (e.g. `'/andar_bahar/time'`). That endpoint is `requireAuth` but NOT
 *            lock-gated, so a dev-PIN session on a dark game can still sync.
 */
export function createClockSync(url: string): ClockSync {
  let bestOffset = 0;
  let bestRtt = Infinity;
  let synced = false;          // a real /time sample has landed
  let seeded = false;          // only a /state fallback has landed
  let dead = false;
  let inflight: Promise<boolean> | null = null;
  const stops: Array<() => void> = [];

  async function oneSample(): Promise<{ offset: number; rtt: number } | null> {
    const t0 = performance.now();
    try {
      // api.get → auto-refresh on 401, and it unwraps `{ok, data}` for us.
      const j = await api.get<{ now: number }>(url);
      const t1 = performance.now();
      const serverEpochMs = Number(j?.now);
      if (!Number.isFinite(serverEpochMs)) return null;
      return { offset: computeOffset(serverEpochMs, t0, t1), rtt: t1 - t0 };
    } catch {
      // A dropped sample is not an error worth surfacing: the previous offset stands and the
      // next burst tries again. Only a page that has NEVER synced falls back to Date.now().
      return null;
    }
  }

  async function sync(samples = 6): Promise<boolean> {
    if (dead) return false;
    if (inflight) return inflight;                 // never two bursts at once (timer + focus)
    inflight = (async () => {
      let localOffset = bestOffset;
      let localRtt = synced ? bestRtt : Infinity;
      let got = false;
      for (let i = 0; i < samples; i++) {
        if (dead) break;
        const s = await oneSample();
        if (s && s.rtt < localRtt) { localRtt = s.rtt; localOffset = s.offset; got = true; }
        else if (s) got = true;
        await new Promise((res) => setTimeout(res, 50));   // a small gap between probes
      }
      if (!dead && got && localRtt < Infinity) {
        bestOffset = localOffset;
        bestRtt = localRtt;
        synced = true;
        return true;
      }
      return synced;
    })();
    try { return await inflight; } finally { inflight = null; }
  }

  const serverNow = () => (synced || seeded ? applyOffset(performance.now(), bestOffset) : Date.now());

  return {
    sync,
    serverNow,
    // serverMs − (serverNow − performance.now()); written as one subtraction of the offset so
    // the two directions can never disagree.
    toPerf: (serverMs: number) => serverMs - (synced || seeded ? bestOffset : Date.now() - performance.now()),
    toServer: (perfMs: number) => perfMs + (synced || seeded ? bestOffset : Date.now() - performance.now()),
    isSynced: () => synced,
    lastRtt: () => bestRtt,
    offset: () => (synced || seeded ? bestOffset : Date.now() - performance.now()),

    noteFallback(serverEpochMs: number, t0: number, t1: number) {
      if (dead || synced) return;                  // a /time sample outranks any piggyback
      if (!Number.isFinite(serverEpochMs)) return;
      const rtt = t1 - t0;
      if (seeded && rtt >= bestRtt) return;
      bestOffset = computeOffset(serverEpochMs, t0, t1);
      bestRtt = rtt;
      seeded = true;
    },

    startAutoSync(periodMs = 30_000) {
      void sync(8);                                 // more samples up front → a tighter lock
      const timer = window.setInterval(() => { void sync(4); }, periodMs);
      const onVis = () => { if (document.visibilityState === 'visible') void sync(4); };
      document.addEventListener('visibilitychange', onVis);
      const stop = () => {
        window.clearInterval(timer);
        document.removeEventListener('visibilitychange', onVis);
      };
      stops.push(stop);
      return stop;
    },

    destroy() {
      dead = true;
      while (stops.length) { const s = stops.pop(); try { s?.(); } catch { /* already gone */ } }
    },
  };
}

export default createClockSync;
